import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import PasswordInput from '../components/PasswordInput';
import { useAuth } from '../contexts/AuthContext';
import { authUrl, returnPathFor } from '../utils/authReturn';
import './Auth.css';

const MIN_PASSWORD = 12;

function SignUp() {
  const { user, register, verifySignup } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Where to go once the account exists: the page that sent the person here —
  // or, if they came via Sign In, the page that sent them to Sign In.
  const destination = returnPathFor(location);

  const [step, setStep] = useState('details'); // 'details' | 'code'
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [salutationName, setSalutationName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);

  const errorRef = useRef(null);
  const codeHeadingRef = useRef(null);

  useEffect(() => {
    document.title = 'Create your account - Brookline Notepad';
  }, []);

  // Fires as soon as `user` is set, including right after verification, so it
  // must use the same destination as submitCode.
  useEffect(() => {
    if (user) navigate(destination, { replace: true });
  }, [user, navigate, destination]);

  // Move focus to the error summary whenever a new error appears.
  useEffect(() => {
    if (error && errorRef.current) errorRef.current.focus();
  }, [error]);

  // On entering the code step, move focus to its heading so the change isn't
  // silent for screen-reader / keyboard users.
  useEffect(() => {
    if (step === 'code' && codeHeadingRef.current) codeHeadingRef.current.focus();
  }, [step]);

  const submitDetails = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < MIN_PASSWORD) {
      setError(`Password must be at least ${MIN_PASSWORD} characters.`);
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await register(email, fullName, salutationName, password);
      setStatus(`We emailed a 6-digit code to ${email}.`);
      setStep('code');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await verifySignup(email, code);
      navigate(destination, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setError('');
    setStatus('');
    setBusy(true);
    try {
      await register(email, fullName, salutationName, password);
      setStatus('A new code is on its way.');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-page">
      <a href="#main-content" className="auth-skip-link">Skip to main content</a>
      <Header title="Brookline Notepad" />

      <main id="main-content" tabIndex={-1} className="auth-content">
        <div className="auth-card">
          {step === 'details' ? (
            <>
              <h2 className="auth-title">Create your account</h2>
              <p className="auth-subtitle">
                Signing up creates your identity. Access to protected pages is
                granted separately by an administrator.
              </p>

              <form onSubmit={submitDetails} className="auth-form" noValidate>
                <div className="auth-field">
                  <label htmlFor="su-full-name">Your Name</label>
                  <input
                    id="su-full-name"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                    autoComplete="name"
                    placeholder="e.g. Michael Rubenstein"
                  />
                </div>
                <div className="auth-field">
                  <label htmlFor="su-salutation">I&rsquo;ll call you&hellip;</label>
                  <input
                    id="su-salutation"
                    type="text"
                    value={salutationName}
                    onChange={(e) => setSalutationName(e.target.value)}
                    autoComplete="given-name"
                    placeholder="e.g. Michael"
                  />
                </div>
                <div className="auth-field">
                  <label htmlFor="su-email">Email</label>
                  <input
                    id="su-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    inputMode="email"
                    placeholder="you@example.com"
                  />
                </div>
                <PasswordInput
                  id="su-password"
                  label="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  minLength={MIN_PASSWORD}
                  describedBy="su-password-help"
                />
                <p id="su-password-help" className="auth-help">
                  At least {MIN_PASSWORD} characters.
                </p>
                <PasswordInput
                  id="su-confirm-password"
                  label="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  minLength={MIN_PASSWORD}
                />
                {confirmPassword.length > 0 && (
                  <p
                    className={password === confirmPassword ? 'auth-status' : 'auth-mismatch'}
                    role="status"
                    aria-live="polite"
                  >
                    {password === confirmPassword ? 'Passwords match.' : 'Passwords do not match.'}
                  </p>
                )}

                {error && (
                  <div className="auth-error" role="alert" tabIndex={-1} ref={errorRef}>
                    {error}
                  </div>
                )}

                <button type="submit" className="auth-btn" disabled={busy}>
                  {busy ? 'Sending code…' : 'Continue'}
                </button>
              </form>
            </>
          ) : (
            <>
              <h2 className="auth-title" tabIndex={-1} ref={codeHeadingRef}>
                Enter your code
              </h2>
              <p className="auth-subtitle">
                Enter the 6-digit code we emailed you. It expires in 10 minutes.
              </p>

              <form onSubmit={submitCode} className="auth-form" noValidate>
                <div className="auth-field">
                  <label htmlFor="su-code">Verification code</label>
                  <input
                    id="su-code"
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]*"
                    maxLength={6}
                  />
                </div>

                {error && (
                  <div className="auth-error" role="alert" tabIndex={-1} ref={errorRef}>
                    {error}
                  </div>
                )}

                <button type="submit" className="auth-btn" disabled={busy}>
                  {busy ? 'Verifying…' : 'Verify & create account'}
                </button>
                <button type="button" className="auth-forgot-link" onClick={resend} disabled={busy}>
                  Resend code
                </button>
              </form>
            </>
          )}

          <p className="auth-status" role="status" aria-live="polite">{status}</p>

          <button className="auth-link-btn" onClick={() => navigate(authUrl('/signin', location))}>
            Already have an account? Sign in
          </button>
        </div>
      </main>
    </div>
  );
}

export default SignUp;
